import type { HoldingCsvItem } from "@yutai/shared"

const CODE_RE = /^\d{4}$/

// 楽天証券のCSVは Shift_JIS なので TextDecoder で明示デコードする（UTF-8 に保存し直されたものも受け付ける）。
export async function decodeRakutenCsvFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  // 楽天のCSVは Shift_JIS だが、一度Excel等で保存し直すと UTF-8 になることがある。
  // Shift_JIS の日本語は UTF-8 としてはほぼ確実に不正なバイト列になるので、まず厳密に UTF-8 を試す。
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer)
  } catch {
    return new TextDecoder("shift_jis").decode(buffer)
  }
}

// ダブルクォート内のカンマ（"3,577,518" のような数値の桁区切り）を壊さないための最小限のCSV行パーサ。
export function parseCsvLine(line: string): string[] {
  const fields: string[] = []
  let cur = ""
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ",") {
      fields.push(cur)
      cur = ""
    } else {
      cur += ch
    }
  }
  fields.push(cur)
  return fields
}

function toNumber(raw: string | undefined): number {
  if (raw == null) return Number.NaN
  return Number(raw.replace(/,/g, ""))
}

// 楽天証券CSVは「特定口座」「NISA成長投資枠」など複数ブロックで構成され、各ブロックは
// 「銘柄コード」から始まるヘッダー行を持つが列構成（内訳列の有無）はブロックごとに異なる。
// ヘッダー行の列名からその都度インデックスを引き直すことで、ブロック間の列ズレを吸収する。
// 同一銘柄が複数ブロックに存在する場合（特定口座 + NISA枠など）は株数・取得総額を合算し、
// 取得単価は加重平均（取得総額合計 ÷ 株数合計）として1件にまとめる。
export function parseRakutenHoldingsCsv(text: string): HoldingCsvItem[] {
  const totals = new Map<string, { shares: number; amount: number }>()

  let codeIdx = -1
  let sharesIdx = -1
  let totalIdx = -1

  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue
    const fields = parseCsvLine(line).map((f) => f.trim())

    if (fields.includes("銘柄コード")) {
      codeIdx = fields.indexOf("銘柄コード")
      sharesIdx = fields.indexOf("保有数量［株］")
      totalIdx = fields.indexOf("取得総額［円］")
      continue
    }

    if (codeIdx === -1 || sharesIdx === -1 || totalIdx === -1) continue

    const code = fields[codeIdx]
    if (!code || !CODE_RE.test(code)) continue

    const shares = toNumber(fields[sharesIdx])
    const amount = toNumber(fields[totalIdx])
    if (!Number.isFinite(shares) || shares <= 0 || !Number.isFinite(amount) || amount < 0) continue

    const existing = totals.get(code) ?? { shares: 0, amount: 0 }
    existing.shares += shares
    existing.amount += amount
    totals.set(code, existing)
  }

  return [...totals.entries()].map(([code, { shares, amount }]) => ({
    code,
    shares,
    acquisitionPrice: Math.round((amount / shares) * 100) / 100,
  }))
}
